import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import ClubBlock from "./ClubBlock"

type PersistentClubBlockContextValue = {
  registerSlot: (slot: HTMLDivElement) => void
  unregisterSlot: (slot: HTMLDivElement) => void
}

const PersistentClubBlockContext =
  createContext<PersistentClubBlockContextValue | null>(null)

export function PersistentClubBlockProvider({
  clubId,
  children,
}: {
  clubId?: number | null
  children: React.ReactNode
}): JSX.Element {
  const [activeSlot, setActiveSlot] = useState<HTMLDivElement | null>(null)
  const [portalRoot] = useState<HTMLDivElement | null>(() => {
    if (typeof document === "undefined") return null
    return document.createElement("div")
  })
  const pendingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const registerSlot = useCallback((slot: HTMLDivElement) => {
    if (pendingClearRef.current) {
      clearTimeout(pendingClearRef.current)
      pendingClearRef.current = null
    }

    setActiveSlot((currentSlot) => (currentSlot === slot ? currentSlot : slot))
  }, [])

  const unregisterSlot = useCallback((slot: HTMLDivElement) => {
    if (pendingClearRef.current) {
      clearTimeout(pendingClearRef.current)
    }

    pendingClearRef.current = setTimeout(() => {
      setActiveSlot((currentSlot) => (currentSlot === slot ? null : currentSlot))
      pendingClearRef.current = null
    }, 0)
  }, [])

  useEffect(() => {
    return () => {
      if (pendingClearRef.current) {
        clearTimeout(pendingClearRef.current)
      }

      if (portalRoot?.parentNode) {
        portalRoot.parentNode.removeChild(portalRoot)
      }
    }
  }, [portalRoot])

  useLayoutEffect(() => {
    if (!portalRoot) return

    if (activeSlot) {
      activeSlot.appendChild(portalRoot)
      return
    }

    if (portalRoot.parentNode) {
      portalRoot.parentNode.removeChild(portalRoot)
    }
  }, [activeSlot, portalRoot])

  const contextValue = useMemo(
    () => ({ registerSlot, unregisterSlot }),
    [registerSlot, unregisterSlot],
  )

  return (
    <PersistentClubBlockContext.Provider value={contextValue}>
      {children}
      {clubId && portalRoot
        ? createPortal(
            <ClubBlock key={`persistent-club-block-${clubId}`} />,
            portalRoot,
          )
        : null}
    </PersistentClubBlockContext.Provider>
  )
}

export function PersistentClubBlockSlot(): JSX.Element {
  const context = useContext(PersistentClubBlockContext)
  const slotRef = useRef<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    if (!context || !slotRef.current) return

    const slotElement = slotRef.current
    context.registerSlot(slotElement)

    return () => {
      context.unregisterSlot(slotElement)
    }
  }, [context])

  return <div ref={slotRef} />
}